import {useEffect, useMemo, useState} from 'react'
import {Link} from 'react-router-dom'
import {collection, getFirestore, onSnapshot} from 'firebase/firestore'
import type {DocumentData, Timestamp} from 'firebase/firestore'
import {KTCard, KTCardBody, KTSVG} from '../../../../../_metronic/helpers'

const PRINTER_STATUS_COLLECTION = 'printerStatus'
const LIVE_THRESHOLD_MS = 2 * 60 * 1000
const STALE_THRESHOLD_MS = 10 * 60 * 1000

type MaybeTimestamp =
  | Timestamp
  | Date
  | number
  | string
  | {
      seconds?: number
      _seconds?: number
    }
  | null
  | undefined

type MaybeNumber = number | string | null | undefined

type PrinterStatusDocument = {
  cachedAssets?: MaybeNumber
  deviceId?: string
  expiresAt?: MaybeTimestamp
  firmware?: {
    buildDate?: string
    version?: string
  }
  healthScore?: MaybeNumber
  ink?: string
  isOnline?: boolean
  lastHealthCheck?: MaybeTimestamp
  lastPing?: MaybeTimestamp
  lastPingTimestamp?: MaybeTimestamp
  network?: {
    signalStrength?: MaybeNumber
    ssid?: string
  }
  paper?: string
  queuedJobs?: MaybeNumber
  status?: string
  storage?: {
    freeMB?: MaybeNumber
    totalMB?: MaybeNumber
  }
  updatedAt?: MaybeTimestamp
  uptime?: MaybeNumber
}

type PrinterStatus = {
  id: string
  cachedAssets?: number
  deviceId: string
  expiresAtMillis?: number
  firmware: {
    buildDate?: string
    version?: string
  }
  healthScore?: number
  ink?: string
  isOnline?: boolean
  lastHealthCheckMillis?: number
  lastPingMillis?: number
  network: {
    signalStrength?: number
    ssid?: string
  }
  paper?: string
  queuedJobs?: number
  status?: string
  storage: {
    freeMB?: number
    totalMB?: number
  }
  updatedAtMillis?: number
  uptime?: number
}

type ConnectionState = {
  label: 'Live' | 'Stale' | 'Offline'
  badgeClass: string
  dotClass: string
  priority: number
}

type PrinterSeverity = 'normal' | 'info' | 'attention' | 'error' | 'connectivity'

type PrinterStatusDefinition = {
  label: string
  meaning: string
  severity: PrinterSeverity
}

type PrinterCondition = PrinterStatusDefinition & {
  badgeClass: string
  dotClass: string
  priority: number
  severityLabel: string
  textClass: string
}

type PrinterWithConnection = PrinterStatus & {
  connection: ConnectionState
  condition: PrinterCondition
}

type ConditionFilter = 'all' | PrinterSeverity

type SupplyState = {
  badgeClass: string
  label: string
}

const SEVERITY_CONFIG: Record<
  PrinterSeverity,
  {
    badgeClass: string
    dotClass: string
    label: string
    priority: number
    textClass: string
  }
> = {
  error: {
    badgeClass: 'badge-light-danger',
    dotClass: 'bg-danger',
    label: 'Error',
    priority: 1,
    textClass: 'text-danger',
  },
  connectivity: {
    badgeClass: 'badge-light-dark',
    dotClass: 'bg-dark',
    label: 'Connectivity',
    priority: 2,
    textClass: 'text-gray-700',
  },
  attention: {
    badgeClass: 'badge-light-warning',
    dotClass: 'bg-warning',
    label: 'Attention',
    priority: 3,
    textClass: 'text-warning',
  },
  info: {
    badgeClass: 'badge-light-primary',
    dotClass: 'bg-primary',
    label: 'Info',
    priority: 4,
    textClass: 'text-primary',
  },
  normal: {
    badgeClass: 'badge-light-success',
    dotClass: 'bg-success',
    label: 'Normal',
    priority: 5,
    textClass: 'text-success',
  },
}

const PRINTER_STATUS_DEFINITIONS: Record<string, PrinterStatusDefinition> = {
  ok: {
    label: 'OK',
    meaning: 'Printer is idle or printing normally.',
    severity: 'normal',
  },
  'cooling print head': {
    label: 'Cooling Print Head',
    meaning: 'Print head is cooling; printing resumes automatically.',
    severity: 'info',
  },
  'cooling paper motor': {
    label: 'Cooling Paper Motor',
    meaning: 'Paper motor is cooling; printing resumes automatically.',
    severity: 'info',
  },
  'standby mode': {
    label: 'Standby Mode',
    meaning: 'Power-saving standby; wakes on the next job.',
    severity: 'info',
  },
  'cover open': {
    label: 'Cover Open',
    meaning: 'The printer cover is open.',
    severity: 'attention',
  },
  'no scrap box': {
    label: 'No Scrap Box',
    meaning: 'Scrap box removed or door open.',
    severity: 'attention',
  },
  'paper end': {
    label: 'Paper End',
    meaning: 'Out of paper / media.',
    severity: 'attention',
  },
  'ribbon end': {
    label: 'Ribbon End',
    meaning: 'Out of ribbon (ink).',
    severity: 'attention',
  },
  'paper jam': {
    label: 'Paper Jam',
    meaning: 'Paper is jammed; clear it to continue.',
    severity: 'error',
  },
  'ribbon error': {
    label: 'Ribbon Error',
    meaning: 'Ribbon fault (e.g. torn or misloaded).',
    severity: 'error',
  },
  'paper definition error': {
    label: 'Paper Definition Error',
    meaning: 'Loaded media does not match the expected type/size.',
    severity: 'error',
  },
  'data error': {
    label: 'Data Error',
    meaning: 'Print data error.',
    severity: 'error',
  },
  'head voltage error': {
    label: 'Head Voltage Error',
    meaning: 'Print head voltage fault.',
    severity: 'error',
  },
  'usb power supply error': {
    label: 'USB Power Supply Error',
    meaning: 'USB power supply fault.',
    severity: 'error',
  },
  'head position error': {
    label: 'Head Position Error',
    meaning: 'Print head positioning fault.',
    severity: 'error',
  },
  'power supply fan error': {
    label: 'Power Supply Fan Error',
    meaning: 'Cooling fan fault.',
    severity: 'error',
  },
  'cutter error': {
    label: 'Cutter Error',
    meaning: 'Cutter mechanism fault.',
    severity: 'error',
  },
  'pinch roller error': {
    label: 'Pinch Roller Error',
    meaning: 'Pinch roller fault.',
    severity: 'error',
  },
  'abnormal head temperature': {
    label: 'Abnormal Head Temperature',
    meaning: 'Print head temperature out of range.',
    severity: 'error',
  },
  'abnormal media temperature': {
    label: 'Abnormal Media Temperature',
    meaning: 'Media temperature out of range.',
    severity: 'error',
  },
  'abnormal paper motor temperature': {
    label: 'Abnormal Paper Motor Temperature',
    meaning: 'Paper motor temperature out of range.',
    severity: 'error',
  },
  'ribbon tension error': {
    label: 'Ribbon Tension Error',
    meaning: 'Ribbon tension fault.',
    severity: 'error',
  },
  'rf-id module error': {
    label: 'RF-ID Module Error',
    meaning: 'Media RF-ID module fault.',
    severity: 'error',
  },
  'rs422 communiation error': {
    label: 'RS422 Communiation Error',
    meaning: 'Internal RS422 communication fault.',
    severity: 'error',
  },
  'rs422 communication error': {
    label: 'RS422 Communication Error',
    meaning: 'Internal RS422 communication fault.',
    severity: 'error',
  },
  'system error': {
    label: 'System Error',
    meaning: 'General printer system error.',
    severity: 'error',
  },
  'communication failure': {
    label: 'Communication Failure',
    meaning: 'Lost communication with the printer.',
    severity: 'error',
  },
  'unknown error': {
    label: 'Unknown Error',
    meaning: 'Unrecognized printer error.',
    severity: 'error',
  },
  offline: {
    label: 'Offline',
    meaning: 'Printer is unplugged or powered off.',
    severity: 'connectivity',
  },
  'no default printer': {
    label: 'no default printer',
    meaning: 'No printer is configured in the print system.',
    severity: 'error',
  },
  unknown: {
    label: 'unknown',
    meaning: 'Status not yet determined.',
    severity: 'info',
  },
}

const SUPPLY_OK_VALUES = ['available', 'full', 'good', 'loaded', 'normal', 'ok', 'ready']
const SUPPLY_LOW_VALUES = [
  'ink_low',
  'low',
  'marker_supply_low',
  'media_low',
  'near_empty',
  'paper_low',
  'ribbon_low',
  'toner_low',
  'warning',
]
const SUPPLY_EMPTY_VALUES = [
  'empty',
  'error',
  'ink_empty',
  'jam',
  'marker_supply_empty',
  'media_empty',
  'media_empty_error',
  'media_jam',
  'media_needed',
  'missing',
  'out',
  'out_of_paper',
  'paper_end',
  'paper_jam',
  'ribbon_empty',
  'ribbon_end',
  'toner_empty',
  'unavailable',
]

const asFiniteNumber = (value: unknown): number | undefined => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : undefined
  }

  if (typeof value === 'string') {
    const trimmedValue = value.trim()

    if (!trimmedValue) {
      return undefined
    }

    const parsedValue = Number(trimmedValue)
    return Number.isFinite(parsedValue) ? parsedValue : undefined
  }

  return undefined
}

const toMillis = (value: MaybeTimestamp): number | undefined => {
  if (!value) {
    return undefined
  }

  if (typeof value === 'number') {
    return value < 1000000000000 ? value * 1000 : value
  }

  if (typeof value === 'string') {
    const numericValue = asFiniteNumber(value)

    if (numericValue !== undefined) {
      return numericValue < 1000000000000 ? numericValue * 1000 : numericValue
    }

    const parsed = Date.parse(value)
    return Number.isNaN(parsed) ? undefined : parsed
  }

  if (value instanceof Date) {
    return value.getTime()
  }

  const timestampLike = value as {
    toMillis?: () => number
    seconds?: number
    _seconds?: number
  }

  if (typeof timestampLike.toMillis === 'function') {
    return timestampLike.toMillis()
  }

  const seconds = timestampLike.seconds ?? timestampLike._seconds
  return typeof seconds === 'number' ? seconds * 1000 : undefined
}

const toUptimeSeconds = (value: MaybeNumber): number | undefined => {
  const uptime = asFiniteNumber(value)

  if (uptime === undefined) {
    return undefined
  }

  // Device status payloads may send Android elapsed realtime as milliseconds.
  return uptime > 100000000 ? Math.floor(uptime / 1000) : uptime
}

const formatDateTime = (millis?: number): string => {
  if (!millis) {
    return '-'
  }

  return new Date(millis).toLocaleString(undefined, {
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    month: 'short',
    year: 'numeric',
  })
}

const formatRelativeTime = (millis: number | undefined, now: number): string => {
  if (!millis) {
    return 'Never'
  }

  const diff = millis - now
  const absDiff = Math.abs(diff)
  const suffix = diff >= 0 ? 'from now' : 'ago'

  if (absDiff < 1000) {
    return 'Now'
  }

  const minutes = Math.round(absDiff / 60000)

  if (minutes < 1) {
    return `${Math.round(absDiff / 1000)}s ${suffix}`
  }

  if (minutes < 60) {
    return `${minutes}m ${suffix}`
  }

  const hours = Math.round(minutes / 60)

  if (hours < 24) {
    return `${hours}h ${suffix}`
  }

  return `${Math.round(hours / 24)}d ${suffix}`
}

const formatDuration = (seconds?: number): string => {
  if (seconds === undefined) {
    return '-'
  }

  const days = Math.floor(seconds / 86400)
  const hours = Math.floor((seconds % 86400) / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)

  if (days > 0) {
    return `${days}d ${hours}h`
  }

  if (hours > 0) {
    return `${hours}h ${minutes}m`
  }

  return `${minutes}m`
}

const formatValue = (value?: number | string): string => {
  return value === undefined || value === '' ? '-' : String(value)
}

const normalizeLookupKey = (value?: string): string => {
  return (value || '')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_')
}

const normalizeStatusKey = (value?: string): string => {
  return (value || '').trim().toLowerCase().replace(/\s+/g, ' ')
}

const humanizeMachineValue = (value?: string): string => {
  const normalized = (value || '').trim().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ')

  if (!normalized) {
    return '-'
  }

  if (normalized.toLowerCase() === 'ok') {
    return 'OK'
  }

  return normalized.replace(/\b\w/g, (character) => character.toUpperCase())
}

const withSeverityConfig = (definition: PrinterStatusDefinition): PrinterCondition => {
  const severityConfig = SEVERITY_CONFIG[definition.severity]

  return {
    ...definition,
    badgeClass: severityConfig.badgeClass,
    dotClass: severityConfig.dotClass,
    priority: severityConfig.priority,
    severityLabel: severityConfig.label,
    textClass: severityConfig.textClass,
  }
}

const getPrinterCondition = (status?: string): PrinterCondition => {
  const cleanStatus = (status || '').trim()
  const statusDefinition = PRINTER_STATUS_DEFINITIONS[normalizeStatusKey(cleanStatus)]

  if (statusDefinition) {
    return withSeverityConfig(statusDefinition)
  }

  if (!cleanStatus) {
    return withSeverityConfig(PRINTER_STATUS_DEFINITIONS.unknown)
  }

  return withSeverityConfig({
    label: cleanStatus,
    meaning: 'Raw print-system reason from CUPS; waiting for live printer status.',
    severity: 'info',
  })
}

const getSupplyState = (value?: string): SupplyState => {
  const normalized = normalizeLookupKey(value)
  const label = humanizeMachineValue(value)

  if (!normalized || normalized === 'unknown') {
    return {
      badgeClass: 'badge-light-secondary',
      label,
    }
  }

  if (SUPPLY_OK_VALUES.includes(normalized)) {
    return {
      badgeClass: 'badge-light-success',
      label,
    }
  }

  if (SUPPLY_LOW_VALUES.includes(normalized)) {
    return {
      badgeClass: 'badge-light-warning',
      label,
    }
  }

  if (SUPPLY_EMPTY_VALUES.includes(normalized) || normalized.endsWith('_error')) {
    return {
      badgeClass: 'badge-light-danger',
      label,
    }
  }

  return {
    badgeClass: 'badge-light-primary',
    label,
  }
}

const clampPercent = (value?: number): number => {
  return value === undefined ? 0 : Math.max(0, Math.min(100, value))
}

const getConnectionState = (printer: PrinterStatus, now: number): ConnectionState => {
  const lastHeartbeatMillis = printer.lastPingMillis ?? printer.updatedAtMillis
  const heartbeatAge = lastHeartbeatMillis ? now - lastHeartbeatMillis : Number.POSITIVE_INFINITY
  const isExpired = printer.expiresAtMillis ? now > printer.expiresAtMillis : false

  if (printer.isOnline === false || isExpired) {
    return {
      label: 'Offline',
      badgeClass: 'badge-light-danger',
      dotClass: 'bg-danger',
      priority: 2,
    }
  }

  if (printer.isOnline === true && heartbeatAge <= LIVE_THRESHOLD_MS) {
    return {
      label: 'Live',
      badgeClass: 'badge-light-success',
      dotClass: 'bg-success',
      priority: 5,
    }
  }

  if (heartbeatAge <= STALE_THRESHOLD_MS) {
    return {
      label: 'Stale',
      badgeClass: 'badge-light-warning',
      dotClass: 'bg-warning',
      priority: 3,
    }
  }

  return {
    label: 'Offline',
    badgeClass: 'badge-light-danger',
    dotClass: 'bg-danger',
    priority: 2,
  }
}

const getHealthColor = (healthScore?: number): string => {
  if (healthScore === undefined) {
    return 'secondary'
  }

  if (healthScore >= 75) {
    return 'success'
  }

  if (healthScore >= 50) {
    return 'warning'
  }

  return 'danger'
}

const mapPrinter = (id: string, data: DocumentData): PrinterStatus => {
  const raw = data as PrinterStatusDocument

  return {
    id,
    cachedAssets: asFiniteNumber(raw.cachedAssets),
    deviceId: raw.deviceId || id,
    expiresAtMillis: toMillis(raw.expiresAt),
    firmware: {
      buildDate: raw.firmware?.buildDate,
      version: raw.firmware?.version,
    },
    healthScore: asFiniteNumber(raw.healthScore),
    ink: raw.ink,
    isOnline: raw.isOnline,
    lastHealthCheckMillis: toMillis(raw.lastHealthCheck),
    lastPingMillis: toMillis(raw.lastPingTimestamp) ?? toMillis(raw.lastPing),
    network: {
      signalStrength: asFiniteNumber(raw.network?.signalStrength),
      ssid: raw.network?.ssid,
    },
    paper: raw.paper,
    queuedJobs: asFiniteNumber(raw.queuedJobs),
    status: raw.status,
    storage: {
      freeMB: asFiniteNumber(raw.storage?.freeMB),
      totalMB: asFiniteNumber(raw.storage?.totalMB),
    },
    updatedAtMillis: toMillis(raw.updatedAt),
    uptime: toUptimeSeconds(raw.uptime),
  }
}

const matchesSearch = (printer: PrinterWithConnection, searchTerm: string): boolean => {
  if (!searchTerm) {
    return true
  }

  const haystack = [
    printer.deviceId,
    printer.id,
    printer.status,
    printer.condition.meaning,
    printer.condition.severityLabel,
    printer.connection.label,
    printer.ink,
    printer.paper,
    printer.firmware.version,
    printer.firmware.buildDate,
    printer.network.ssid,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()

  return haystack.includes(searchTerm)
}

const PrintersList = () => {
  const [printers, setPrinters] = useState<Array<PrinterStatus>>([])
  const [isLoading, setIsLoading] = useState(true)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [conditionFilter, setConditionFilter] = useState<ConditionFilter>('all')
  const [now, setNow] = useState(Date.now())

  useEffect(() => {
    const intervalId = window.setInterval(() => setNow(Date.now()), 15000)

    return () => window.clearInterval(intervalId)
  }, [])

  useEffect(() => {
    const db = getFirestore()
    const unsubscribe = onSnapshot(
      collection(db, PRINTER_STATUS_COLLECTION),
      (snapshot) => {
        const nextPrinters = snapshot.docs.map((printerDoc) =>
          mapPrinter(printerDoc.id, printerDoc.data())
        )
        setPrinters(nextPrinters)
        setIsLoading(false)
        setErrorMessage(null)
      },
      (error) => {
        setErrorMessage(error.message || 'Unable to load printer status.')
        setIsLoading(false)
      }
    )

    return unsubscribe
  }, [])

  const printersWithConnection = useMemo<Array<PrinterWithConnection>>(() => {
    return printers
      .map((printer) => ({
        ...printer,
        connection: getConnectionState(printer, now),
        condition: getPrinterCondition(printer.status),
      }))
      .sort((a, b) => {
        const aPriority = Math.min(a.condition.priority, a.connection.priority)
        const bPriority = Math.min(b.condition.priority, b.connection.priority)

        if (aPriority !== bPriority) {
          return aPriority - bPriority
        }

        if (a.condition.priority !== b.condition.priority) {
          return a.condition.priority - b.condition.priority
        }

        const pingDiff = (b.lastPingMillis ?? 0) - (a.lastPingMillis ?? 0)

        if (pingDiff !== 0) {
          return pingDiff
        }

        return a.deviceId.localeCompare(b.deviceId)
      })
  }, [now, printers])

  const normalizedSearchTerm = searchTerm.trim().toLowerCase()

  const filteredPrinters = useMemo(() => {
    return printersWithConnection.filter((printer) => {
      const conditionMatches =
        conditionFilter === 'all' ||
        printer.condition.severity === conditionFilter ||
        (conditionFilter === 'connectivity' && printer.connection.label === 'Offline')

      return conditionMatches && matchesSearch(printer, normalizedSearchTerm)
    })
  }, [conditionFilter, normalizedSearchTerm, printersWithConnection])

  const summary = useMemo(() => {
    return {
      attention: printersWithConnection.filter(
        (printer) => printer.condition.severity === 'attention'
      ).length,
      cachedAssets: printersWithConnection.reduce(
        (total, printer) => total + (printer.cachedAssets ?? 0),
        0
      ),
      connectivity: printersWithConnection.filter(
        (printer) =>
          printer.condition.severity === 'connectivity' || printer.connection.label === 'Offline'
      ).length,
      error: printersWithConnection.filter((printer) => printer.condition.severity === 'error')
        .length,
      info: printersWithConnection.filter((printer) => printer.condition.severity === 'info')
        .length,
      live: printersWithConnection.filter((printer) => printer.connection.label === 'Live').length,
      normal: printersWithConnection.filter((printer) => printer.condition.severity === 'normal')
        .length,
      offline: printersWithConnection.filter((printer) => printer.connection.label === 'Offline')
        .length,
      queuedJobs: printersWithConnection.reduce(
        (total, printer) => total + (printer.queuedJobs ?? 0),
        0
      ),
      total: printersWithConnection.length,
    }
  }, [printersWithConnection])

  const conditionFilters: Array<{count: number; label: string; value: ConditionFilter}> = [
    {count: summary.total, label: 'All', value: 'all'},
    {count: summary.error, label: 'Error', value: 'error'},
    {count: summary.attention, label: 'Attention', value: 'attention'},
    {count: summary.connectivity, label: 'Connectivity', value: 'connectivity'},
    {count: summary.info, label: 'Info', value: 'info'},
    {count: summary.normal, label: 'OK', value: 'normal'},
  ]

  return (
    <KTCard>
      <div className='card-header border-0 pt-6'>
        <div className='card-title'>
          <div className='d-flex align-items-center position-relative my-1'>
            <KTSVG
              path='/media/icons/duotune/general/gen021.svg'
              className='svg-icon-1 position-absolute ms-6'
            />
            <input
              type='text'
              className='form-control form-control-solid w-250px fs-7 ps-14'
              placeholder='Search printers'
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
            />
            {searchTerm && (
              <button
                type='button'
                className='btn btn-clear d-flex align-items-center'
                style={{marginLeft: -50}}
                onClick={() => setSearchTerm('')}
              >
                <KTSVG path='/media/icons/duotune/arrows/arr061.svg' className='svg-icon-2' />
              </button>
            )}
          </div>
        </div>
        <div className='card-toolbar flex-wrap gap-2 justify-content-end'>
          <span className='text-muted fw-bold fs-7 me-1'>Condition</span>
          {conditionFilters.map((filter) => (
            <button
              key={filter.value}
              type='button'
              className={`btn btn-sm ${
                conditionFilter === filter.value ? 'btn-primary' : 'btn-light-primary'
              }`}
              onClick={() => setConditionFilter(filter.value)}
            >
              {filter.label}
              <span className='badge badge-circle badge-light ms-2'>{filter.count}</span>
            </button>
          ))}
        </div>
      </div>

      <KTCardBody className='py-4'>
        {errorMessage && (
          <div className='alert alert-danger d-flex align-items-center p-5 mb-6'>
            <KTSVG path='/media/icons/duotune/general/gen044.svg' className='svg-icon-2hx me-4' />
            <div className='fw-bold'>{errorMessage}</div>
          </div>
        )}

        <div className='d-flex flex-wrap gap-4 mb-6'>
          <div className='border border-gray-300 rounded p-4 min-w-150px'>
            <div className='text-muted fw-bold fs-7'>Total printers</div>
            <div className='fs-2 fw-bolder text-dark'>{summary.total}</div>
          </div>
          <div className='border border-gray-300 rounded p-4 min-w-150px'>
            <div className='text-muted fw-bold fs-7'>Live now</div>
            <div className='fs-2 fw-bolder text-success'>{summary.live}</div>
          </div>
          <div className='border border-gray-300 rounded p-4 min-w-150px'>
            <div className='text-muted fw-bold fs-7'>Error statuses</div>
            <div className='fs-2 fw-bolder text-danger'>{summary.error}</div>
          </div>
          <div className='border border-gray-300 rounded p-4 min-w-150px'>
            <div className='text-muted fw-bold fs-7'>Attention</div>
            <div className='fs-2 fw-bolder text-warning'>{summary.attention}</div>
          </div>
          <div className='border border-gray-300 rounded p-4 min-w-150px'>
            <div className='text-muted fw-bold fs-7'>Offline</div>
            <div className='fs-2 fw-bolder text-gray-700'>{summary.offline}</div>
          </div>
          <div className='border border-gray-300 rounded p-4 min-w-150px'>
            <div className='text-muted fw-bold fs-7'>Queued jobs</div>
            <div className='fs-2 fw-bolder text-primary'>{summary.queuedJobs}</div>
          </div>
          <div className='border border-gray-300 rounded p-4 min-w-150px'>
            <div className='text-muted fw-bold fs-7'>Cached assets</div>
            <div className='fs-2 fw-bolder text-dark'>{summary.cachedAssets}</div>
          </div>
        </div>

        <div className='table-responsive'>
          <table className='table fs-6 gy-5 dataTable table-row-bordered table-row-gray-100 align-middle gs-0'>
            <thead>
              <tr className='text-start text-muted fw-bolder fs-7 text-uppercase gs-0'>
                <th className='min-w-150px'>Printer</th>
                <th className='min-w-190px'>Status</th>
                <th className='min-w-160px'>Connection</th>
                <th className='min-w-140px'>Health</th>
                <th className='min-w-150px'>Supplies</th>
                <th className='min-w-120px'>Jobs / Cache</th>
                <th className='min-w-150px'>Network</th>
                <th className='min-w-140px'>Storage</th>
                <th className='min-w-150px'>Firmware</th>
                <th className='min-w-160px'>Updated</th>
              </tr>
            </thead>
            <tbody className='text-gray-600 fw-bold'>
              {filteredPrinters.length > 0 ? (
                filteredPrinters.map((printer) => {
                  const condition = printer.condition
                  const healthColor = getHealthColor(printer.healthScore)
                  const healthPercent = clampPercent(printer.healthScore)
                  const inkState = getSupplyState(printer.ink)
                  const paperState = getSupplyState(printer.paper)
                  const storageTotal = printer.storage.totalMB
                  const storageFree = printer.storage.freeMB
                  const usedStoragePercent =
                    storageTotal && storageFree !== undefined
                      ? Math.max(
                          0,
                          Math.min(
                            100,
                            Math.round(((storageTotal - storageFree) / storageTotal) * 100)
                          )
                        )
                      : undefined

                  return (
                    <tr key={printer.id} className='cursor-pointer'>
                      <td>
                        <div className='d-flex flex-column'>
                          <Link
                            to={`/apps/printer-management/printers/${encodeURIComponent(
                              printer.deviceId
                            )}`}
                            className='text-dark fw-bolder text-hover-primary fs-6'
                          >
                            {printer.deviceId}
                          </Link>
                          <span className='text-muted fw-bold fs-7'>{printer.id}</span>
                        </div>
                      </td>
                      <td>
                        <div className='d-flex flex-column align-items-start'>
                          <div className='d-flex align-items-center mb-1'>
                            <span
                              className={`bullet bullet-dot h-8px w-8px me-2 ${condition.dotClass}`}
                            />
                            <span
                              className={`badge ${condition.badgeClass} text-wrap text-start lh-sm mw-200px`}
                            >
                              {condition.label}
                            </span>
                          </div>
                          <div className={`${condition.textClass} fs-7 fw-bolder`}>
                            {condition.severityLabel}
                          </div>
                          <div className='text-muted fs-8 mw-250px' title={condition.meaning}>
                            {condition.meaning}
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className='d-flex align-items-center mb-1'>
                          <span
                            className={`bullet bullet-dot h-8px w-8px me-2 ${printer.connection.dotClass}`}
                          />
                          <span className={`badge ${printer.connection.badgeClass}`}>
                            {printer.connection.label}
                          </span>
                        </div>
                        <div
                          className='text-muted fs-7'
                          title={formatDateTime(printer.lastPingMillis)}
                        >
                          Ping {formatRelativeTime(printer.lastPingMillis, now)}
                        </div>
                        <div
                          className='text-muted fs-8'
                          title={formatDateTime(printer.expiresAtMillis)}
                        >
                          TTL {formatRelativeTime(printer.expiresAtMillis, now)}
                        </div>
                      </td>
                      <td>
                        <div className='d-flex align-items-center'>
                          <span className={`text-${healthColor} fw-bolder me-3`}>
                            {printer.healthScore !== undefined ? `${printer.healthScore}%` : '-'}
                          </span>
                          <div className='progress h-6px w-100px'>
                            <div
                              className={`progress-bar bg-${healthColor}`}
                              role='progressbar'
                              style={{width: `${healthPercent}%`}}
                            />
                          </div>
                        </div>
                        <div
                          className='text-muted fs-7'
                          title={formatDateTime(printer.lastHealthCheckMillis)}
                        >
                          Check {formatRelativeTime(printer.lastHealthCheckMillis, now)}
                        </div>
                      </td>
                      <td>
                        <div className='d-flex flex-column align-items-start gap-2'>
                          <span className={`badge ${inkState.badgeClass}`}>
                            Ink {inkState.label}
                          </span>
                          <span className={`badge ${paperState.badgeClass}`}>
                            Paper {paperState.label}
                          </span>
                        </div>
                      </td>
                      <td>
                        <div className='text-dark fw-bolder'>{printer.queuedJobs ?? 0}</div>
                        <div className='text-muted fs-7'>Cached {printer.cachedAssets ?? 0}</div>
                      </td>
                      <td>
                        <div className='text-dark fw-bolder'>
                          {formatValue(printer.network.ssid)}
                        </div>
                        <div className='text-muted fs-7'>
                          Signal{' '}
                          {printer.network.signalStrength !== undefined
                            ? `${printer.network.signalStrength}%`
                            : '-'}
                        </div>
                      </td>
                      <td>
                        <div className='text-dark fw-bolder'>
                          {storageFree !== undefined && storageTotal !== undefined
                            ? `${storageFree}/${storageTotal} MB free`
                            : '-'}
                        </div>
                        <div className='progress h-6px w-100px mt-2'>
                          <div
                            className='progress-bar bg-primary'
                            role='progressbar'
                            style={{width: `${usedStoragePercent ?? 0}%`}}
                          />
                        </div>
                      </td>
                      <td>
                        <div className='text-dark fw-bolder'>
                          {formatValue(printer.firmware.version)}
                        </div>
                        <div className='text-muted fs-7'>
                          Build {formatValue(printer.firmware.buildDate)}
                        </div>
                        <div className='text-muted fs-8'>
                          Uptime {formatDuration(printer.uptime)}
                        </div>
                      </td>
                      <td>
                        <div
                          className='text-dark fw-bolder'
                          title={formatDateTime(printer.updatedAtMillis)}
                        >
                          {formatRelativeTime(printer.updatedAtMillis, now)}
                        </div>
                        <div className='text-muted fs-7'>
                          {formatDateTime(printer.updatedAtMillis)}
                        </div>
                      </td>
                    </tr>
                  )
                })
              ) : (
                <tr>
                  <td colSpan={10}>
                    <div className='d-flex text-center w-100 align-content-center justify-content-center py-10'>
                      {isLoading ? 'Loading printers...' : 'No matching printers found'}
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {isLoading && (
          <div className='d-flex align-items-center justify-content-center py-10'>
            <span className='spinner-border text-primary' />
          </div>
        )}
      </KTCardBody>
    </KTCard>
  )
}

const PrintersListWrapper = () => <PrintersList />

export {PrintersListWrapper}
